<script lang="ts">
  // The « Fichiers » tab of a conversation: the list (filter and name search) on the left,
  // the preview of the chosen file on the right. Below 760 px, one or the other.
  import { api, ApiError, type Conversation, type FileItem } from '../lib/api';
  import { dayAndTime, formatSize } from '../lib/format';
  import { filesPath, router } from '../lib/router.svelte';
  import FilePreview from './FilePreview.svelte';

  let { conversation, fileId, onTotal }: { conversation: Conversation; fileId: string | null; onTotal: (n: number) => void } =
    $props();

  type Kind = 'documents' | 'images' | 'all';
  let kind = $state<Kind>('all');
  let query = $state('');
  let items = $state<FileItem[] | null>(null);
  let more = $state(false);
  let error = $state('');
  const PAGE = 100;

  const selected = $derived(items?.find((f) => f.id === fileId) ?? null);

  async function load(append = false) {
    error = '';
    try {
      const before = append ? items?.at(-1)?.id : undefined;
      const page = await api.files(conversation.id, { kind, q: query.trim(), before });
      items = append && items ? [...items, ...page.items] : page.items;
      more = page.items.length === PAGE;
      onTotal(page.total);
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'Impossible de charger les fichiers';
    }
  }

  // Reload on a filter change; the name search waits for a pause in typing.
  $effect(() => {
    void conversation.id;
    void kind;
    const q = query;
    const id = setTimeout(() => void load(), q ? 250 : 0);
    return () => clearTimeout(id);
  });

  function family(file: FileItem): string {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (file.mime === 'application/pdf') return 'pdf';
    if (file.mime.startsWith('image/')) return 'image';
    if (['doc', 'docx', 'odt', 'rtf', 'md', 'txt'].includes(ext)) return 'doc';
    if (['xls', 'xlsx', 'ods', 'csv'].includes(ext)) return 'sheet';
    return 'other';
  }
  const label = (file: FileItem) =>
    file.name.includes('.') ? (file.name.split('.').pop() ?? '').slice(0, 4).toUpperCase() : 'FICH';
</script>

<div class="files" class:has-selection={!!fileId}>
  <div class="list-pane">
    <div class="tools">
      <label class="sr-only" for="file-search">Rechercher un fichier par son nom</label>
      <input id="file-search" class="input" type="search" placeholder="Rechercher un fichier" bind:value={query} />
      <div class="segmented" role="radiogroup" aria-label="Type de fichier">
        {#each [['documents', 'Documents'], ['images', 'Images'], ['all', 'Tout']] as [value, text] (value)}
          <button type="button" role="radio" aria-checked={kind === value} onclick={() => (kind = value as Kind)}>{text}</button>
        {/each}
      </div>
    </div>
    {#if error}<p class="error" role="alert">{error}</p>{/if}
    {#if items === null}
      <p class="status">Chargement…</p>
    {:else if items.length === 0}
      <p class="status">{query.trim() ? `Aucun fichier dont le nom contient « ${query.trim()} »` : 'Aucun fichier dans cette conversation.'}</p>
    {:else}
      <ul class="list">
        {#each items as file (file.id)}
          <li>
            <a
              href={filesPath(conversation.id, file.id)}
              class="item"
              aria-current={file.id === fileId ? 'true' : undefined}
              onclick={(e) => {
                e.preventDefault();
                router.go(filesPath(conversation.id, file.id));
              }}
            >
              <span class="ext {family(file)}" aria-hidden="true">{label(file)}</span>
              <span class="text">
                <span class="name">{file.name}</span>
                <span class="meta">{file.author.display_name} · {dayAndTime(file.created_at)} · {formatSize(file.size_bytes)}</span>
              </span>
            </a>
          </li>
        {/each}
      </ul>
      {#if more}<button type="button" class="btn more" onclick={() => load(true)}>Afficher plus</button>{/if}
    {/if}
  </div>
  <div class="preview-pane">
    {#if selected}
      <button type="button" class="back" onclick={() => router.go(filesPath(conversation.id))}>
        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        Fichiers
      </button>
      <FilePreview file={selected} />
    {:else}
      <p class="status empty">{fileId && items ? 'Ce fichier ne fait pas partie de la liste affichée.' : 'Choisissez un fichier pour le voir ici.'}</p>
    {/if}
  </div>
</div>

<style>
  .files {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(260px, 340px) minmax(0, 1fr);
  }
  .list-pane {
    min-height: 0;
    overflow-y: auto;
    border-right: 1px solid var(--border);
    display: flex;
    flex-direction: column;
  }
  .tools {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    background: var(--bg);
    border-bottom: 1px solid var(--border);
  }
  .tools .input {
    height: 34px;
  }
  .segmented {
    display: flex;
    gap: 4px;
  }
  .segmented button {
    flex: 1;
    height: 30px;
    border: 1px solid var(--border);
    border-radius: 15px;
    background: var(--bg);
    color: var(--ink-2);
    font-size: 13px;
  }
  .segmented button[aria-checked='true'] {
    background: var(--accent-soft);
    border-color: var(--accent-border);
    color: var(--accent-on-soft);
    font-weight: 600;
  }
  .list {
    list-style: none;
    margin: 0;
    padding: 6px;
  }
  .item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    border-radius: var(--radius-m);
    color: inherit;
    text-decoration: none;
  }
  .item:hover {
    background: var(--bg-hover);
  }
  .item[aria-current='true'] {
    background: var(--accent-selected);
  }
  .ext {
    flex: 0 0 36px;
    height: 36px;
    border-radius: var(--radius-s);
    background: var(--bg-page);
    color: var(--ink-2);
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .ext.pdf {
    background: var(--danger-soft);
    color: var(--danger);
  }
  .ext.doc {
    background: var(--status-doing-soft);
    color: var(--status-doing-text);
  }
  .ext.sheet {
    background: var(--status-done-soft);
    color: var(--status-done);
  }
  .ext.image {
    background: var(--accent-soft);
    color: var(--accent-on-soft);
  }
  .text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .name {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .meta {
    font-size: 12px;
    color: var(--muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .more {
    margin: 4px 12px 12px;
  }
  .preview-pane {
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .status {
    margin: 16px;
    color: var(--muted);
    font-size: 13px;
  }
  .empty {
    margin: auto;
  }
  .error {
    margin: 12px;
  }
  .back {
    display: none;
  }
  @media (max-width: 759px) {
    .files {
      grid-template-columns: minmax(0, 1fr);
    }
    .list-pane {
      border-right: 0;
    }
    .has-selection .list-pane,
    .files:not(.has-selection) .preview-pane {
      display: none;
    }
    .back {
      display: flex;
      align-items: center;
      gap: 4px;
      height: 44px;
      padding: 0 12px;
      border: 0;
      background: transparent;
      color: var(--accent-text);
      font-weight: 600;
    }
  }
</style>
